import { appointmentTypeKey } from '@/utils/appointmentFrequency';

import {
  formatScheduleDay,
  scheduleCalendarDay,
  upcomingScheduleDates
} from '../../scheduleLetter';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

export type PrintLetterScheduleRow = {
  type?: string | null;
  scheduledDate?: string | null;
  frequencyNumber?: number | string | null;
  frequency?: number | string | null;
};

/** Remote check types. In clinic and physician request stay off the letter. */
const REMOTE_SCHEDULE_TYPES = new Set(['routine', 'heart failure', 'combo', 'eri tracking']);

export function isRemoteScheduleRow(row: PrintLetterScheduleRow): boolean {
  return REMOTE_SCHEDULE_TYPES.has(appointmentTypeKey(row.type));
}

/** Remote check dates. Heart failure, combo, and ERI are included with routine. */
export function routineScheduleDays(rows: PrintLetterScheduleRow[]): string[] {
  return rows
    .filter(isRemoteScheduleRow)
    .map((row) => row.scheduledDate)
    .filter((day): day is string => Boolean(day));
}

function frequencyDays(row: PrintLetterScheduleRow): number | null {
  for (const raw of [row.frequencyNumber, row.frequency]) {
    if (raw === null || raw === undefined || raw === '') continue;
    const number = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (Number.isFinite(number) && number > 0) return Math.round(number);
  }
  return null;
}

function addUtcDays(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  const nextYear = date.getUTCFullYear();
  const nextMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getUTCDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

/**
 * Upcoming remote check dates, MM-DD-YYYY. When there is exactly one, the next two are that date
 * plus its frequency (in days), then plus the frequency again.
 */
export function printLetterScheduleDates(
  rows: PrintLetterScheduleRow[],
  today = new Date()
): string[] {
  const routine = rows.filter(isRemoteScheduleRow);
  const upcoming = upcomingScheduleDates(
    routine.map((row) => row.scheduledDate),
    today
  );
  if (upcoming.length !== 1) return upcoming;

  const match = routine.find((row) => {
    const day = scheduleCalendarDay(row.scheduledDate);
    return Boolean(day && formatScheduleDay(day) === upcoming[0] && frequencyDays(row));
  });
  const frequency = match ? frequencyDays(match) : null;
  const start = match ? scheduleCalendarDay(match.scheduledDate) : null;
  if (!frequency || !start) return upcoming;

  return [
    upcoming[0],
    formatScheduleDay(addUtcDays(start, frequency)),
    formatScheduleDay(addUtcDays(start, frequency * 2))
  ];
}

/**
 * The next routine schedule dates from today on, as MM-DD-YYYY (same format as the mailed
 * schedule letter). Reads Scheduled/Pending pages until four real dates are found.
 */
export async function loadPrintLetterDates(
  loadPage: (page: number, limit: number) => Promise<PrintLetterScheduleRow[]>,
  today = new Date()
): Promise<string[]> {
  const collected: PrintLetterScheduleRow[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const rows = await loadPage(page, PAGE_SIZE);
    collected.push(...rows);
    const upcoming = upcomingScheduleDates(routineScheduleDays(collected), today);
    if (upcoming.length >= 4 || rows.length < PAGE_SIZE) {
      return printLetterScheduleDates(collected, today);
    }
  }
  return printLetterScheduleDates(collected, today);
}
